import { LIGHT_STRIDE, LightingView, NO_STAMPS, newLightingFrame, type LightingFrame } from "daedalus-renderer-phaser";
import { MAX_LIGHTS_PER_VIEW, MAX_MAP_DIM } from "daedalus-core";
import { INDOOR_LIGHT_EASE, ROOF_LIGHT_EASE, ambientAt, ambientAtMinute, easedLevel, gameDayOf, indoorLight, newAmbient, nightLevel, withWeatherDim, type Ambient } from "odyssey-shared";
import type { WorldUiModule } from "../hook.ts";
import { renderedView } from "../mount.ts";
import { serverNow } from "../../server-clock.ts";
import type { LightingControl } from "./control.ts";
import { PAGE_MAP_LIGHTS, placeMapLights, type MapLightHolder } from "./map-lights.ts";
import { mountLightSlider } from "./slider.ts";

/**
 * The minute of the turn a room is lit at before the page knows the time: full day.
 */
const NOON_MINUTE = 18;


/**
 * The light layer as a world-UI module (ADR-141 and its amendment of 2026-10-31): the sky's ambient and tint from the
 * server's clock and the sky override, or an indoor map's fixed dim, and the lights in view, all handed to a `LightingView`
 * in the world layer. The darkness draws over the world and under every canvas surface, which the UI camera draws, so
 * nameplates, bars, numbers and bubbles stay bright. It runs before the sharp zoom, so the buffer that module draws holds the
 * current darkness. The lights are the booted map's lit props (`map-lights.ts`, ADR-141's amendment of 2026-11-01), which
 * flicker unless Reduced motion is on. With `?lights=dev` it also mounts the developer slider (`slider.ts`).
 *
 * @param control The page's lighting control.
 * @param map The booted map's lights, which the scene fills.
 * @param motion The page's Reduced motion setting; lights hold steady under it.
 * @param now The server's time in milliseconds, or null before a welcome has told the page it; the page's own wall clock is never read, so it is never night for one player and day for another.
 * @returns The module, to list before the sharp zoom.
 */
export function lighting(control: LightingControl, map: MapLightHolder = PAGE_MAP_LIGHTS, motion: { reduced(): boolean } = { reduced: () => false }, now: () => number | null = serverNow): WorldUiModule {
  return {
    id: "lighting",
    /**
     * Makes the view and the buffers a frame writes into.
     *
     * @param context The scene and the world camera.
     * @returns The surface, which draws the light layer each frame.
     */
    create(context) {
      const view = new LightingView(context.scene);
      const frame: LightingFrame = newLightingFrame();
      const sky: Ambient = newAmbient();
      const camera = { width: 0, height: 0, originX: 0, originY: 0, zoom: 1, scrollX: 0, scrollY: 0 };
      const slider = control.slider ? mountLightSlider(control) : null;
      const lightSky = { ambient: 1, nightLevel: 0.5, indoor: false };
      return {
        /**
         * Reads the light, places the lights and draws the frame.
         */
        update() {
          if (!control.enabled) {
            frame.tint = 0;
            frame.ambient = 1;
            frame.lightCount = 0;
            view.frame(frame);
            return;
          }
          const time = now();
          control.serverTime = time;
          if (time === null && control.minute === null && control.indoor === null) {
            frame.tint = 0;
            frame.ambient = 1;
            frame.lightCount = 0;
            view.frame(frame);
            return;
          }
          const day = control.day ?? (time === null ? 0 : gameDayOf(time));
          if (control.indoor !== null) {
            if (control.minute !== null) {
              ambientAtMinute(control.minute, day, "none", sky);
            } else if (time !== null) {
              ambientAt(time, "none", sky);
            } else {
              ambientAtMinute(NOON_MINUTE, day, "none", sky);
            }
            indoorLight(control.indoor.dim, sky);
            frame.nightLevel = Math.min(1 - Math.min(MAX_MAP_DIM, Math.max(0, control.indoor.dim)), easedLevel(nightLevel(day, "none"), INDOOR_LIGHT_EASE));
          } else {
            if (control.minute === null) {
              ambientAt(time as number, control.sky, sky);
            } else {
              ambientAtMinute(control.minute, day, control.sky, sky);
            }
            withWeatherDim(sky, control.weatherDim, control.sky);
            frame.nightLevel = nightLevel(day, control.sky);
          }
          frame.easeStamps = control.indoor === null ? control.roofStamps : NO_STAMPS;
          frame.easeShare = ROOF_LIGHT_EASE;
          renderedView(context.worldCamera, camera);
          const worldWidth = camera.width / camera.zoom;
          const worldHeight = camera.height / camera.zoom;
          frame.left = camera.scrollX + camera.width / 2 - worldWidth / 2;
          frame.top = camera.scrollY + camera.height / 2 - worldHeight / 2;
          frame.width = worldWidth;
          frame.height = worldHeight;
          frame.deviceWidth = camera.width;
          frame.deviceHeight = camera.height;
          frame.ambient = sky.ambient;
          frame.r = sky.r;
          frame.g = sky.g;
          frame.b = sky.b;
          frame.tint = sky.tint;
          frame.time = time ?? 0;
          frame.flicker = !motion.reduced();
          lightSky.ambient = sky.ambient;
          lightSky.nightLevel = frame.nightLevel;
          lightSky.indoor = control.indoor !== null;
          frame.lightCount = control.lights === "bench" ? placeBenchLights(frame) : control.lights === "map" && map.lights !== null ? placeMapLights(map.lights, frame, lightSky, map.show) : 0;
          view.frame(frame);
          control.view.left = frame.left;
          control.view.top = frame.top;
          control.view.width = frame.width;
          control.view.height = frame.height;
          control.view.deviceWidth = frame.deviceWidth;
          control.view.deviceHeight = frame.deviceHeight;
          control.ambient = sky.ambient;
          control.tint = sky.tint;
          control.fills = view.stats.fills;
          control.cuts = view.stats.cuts;
          control.glows = view.stats.glows;
          control.mapLights = map.lights?.count ?? 0;
          control.unlitShown = map.lights?.unlitShown[0] ?? 0;
          slider?.update();
        },
        /**
         * Removes the view and the slider.
         */
        destroy() {
          slider?.destroy();
          view.destroy();
        },
      };
    },
  };
}

/**
 * Lays `MAX_LIGHTS_PER_VIEW` lights in a grid over the view, for the browser suite's allocation case.
 *
 * @param frame The frame, whose view rectangle is set.
 * @returns The lights written.
 */
function placeBenchLights(frame: LightingFrame): number {
  const columns = 8;
  for (let index = 0; index < MAX_LIGHTS_PER_VIEW; index += 1) {
    const at = index * LIGHT_STRIDE;
    frame.lights[at] = frame.left + ((index % columns) + 0.5) * (frame.width / columns);
    frame.lights[at + 1] = frame.top + (Math.floor(index / columns) + 0.5) * (frame.height / (MAX_LIGHTS_PER_VIEW / columns));
    frame.lights[at + 2] = 40;
    frame.lights[at + 3] = 255;
    frame.lights[at + 4] = 190;
    frame.lights[at + 5] = 110;
    frame.lights[at + 6] = 1;
    frame.lights[at + 7] = 0.25;
    frame.lights[at + 8] = 6;
    frame.lights[at + 9] = index;
    frame.lights[at + 10] = 1;
    frame.lights[at + 11] = 2.5;
  }
  return MAX_LIGHTS_PER_VIEW;
}
